import { useEffect, useState } from "react";
import {
  Typography,
  Card,
  Form,
  Input,
  Button,
  Alert,
  Flex,
  Grid,
  message,
} from "antd";
import { Search, TriangleAlert } from "lucide-react";
import type {
  CheckRecordRequest,
  CheckRecordResponse,
} from "@/features/public/types/public";
import { useLoading } from "@/hooks/use-loading";
import { checkRecord } from "@/services/public.service";
import {
  getIdentityType,
  isValidIdentity,
  normalizeIdentity,
} from "./utils/check-record.utils";

type CheckRecordProps = {
  onVerified: (
    clean: string,
    nextView: "category" | "status",
    record: CheckRecordResponse | null,
  ) => void;
};

const { Text, Title } = Typography;
const { useBreakpoint } = Grid;

export function CheckRecord({ onVerified }: CheckRecordProps) {
  const [error, setError] = useState(false);
  const { loading, withLoading } = useLoading();
  const [form] = Form.useForm<CheckRecordRequest>();
  const screens = useBreakpoint();

  useEffect(() => {
    window.sessionStorage.removeItem("public-registration-flow");
  }, []);

  const identityNo = Form.useWatch("identityNo", form);

  const hasIdentityNo = isValidIdentity(identityNo);

  const check = async (values: CheckRecordRequest) => {
    const clean = normalizeIdentity(values.identityNo);
    const identityTypeKey = getIdentityType(clean);

    if (!identityTypeKey) {
      return setError(true);
    }

    try {
      const res = await withLoading(() =>
        checkRecord({
          identityNo: clean,
          identityTypeKey: identityTypeKey,
        }),
      );
      const { hasRecord } = res.data;

      setError(false);

      if (hasRecord) {
        onVerified(clean, "status", res.data);
      } else {
        onVerified(clean, "category", null);
      }
    } catch (error) {
      console.error(error);
      message.error("Pengesahan gagal. Sila cuba lagi.");
    }
  };

  return (
    <>
      <Title
        level={2}
        style={{
          marginTop: 16,
          textAlign: "center",
          fontWeight: 700,
          fontSize: screens.sm ? 32 : 24,
          color: "var(--color-primary)",
        }}
      >
        Pendaftaran
      </Title>

      <Card
        variant="borderless"
        style={{
          margin: "48px auto 0",
          borderRadius: 16,
          maxWidth: 576,
          width: "100%",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.01)",
        }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={check}
          onValuesChange={() => {
            if (error) setError(false);
          }}
        >
          <Flex vertical gap="40px">
            <Flex vertical gap="8px">
              {error && (
                <Alert
                  type="error"
                  showIcon
                  icon={
                    <TriangleAlert
                      size={18}
                      style={{ color: "#C00" }}
                      aria-hidden="true"
                    />
                  }
                  title="Nombor MYKAD / Passport tidak sah"
                  style={{
                    color: "#CC0000",
                    marginBottom: 8,
                    borderColor: "#C00",
                    backgroundColor: "#FCC",
                    fontWeight: 500,
                    padding: "8px 16px",
                  }}
                />
              )}

              <Form.Item
                name="identityNo"
                style={{ marginBottom: 0 }}
                getValueFromEvent={(event) =>
                  String(event.target.value)
                    .replace(/[^a-zA-Z0-9-\s]/g, "")
                    .replace(/[-\s]+/g, "")
                    .toUpperCase()
                }
              >
                <Input
                  size="large"
                  placeholder="Nombor MyKad atau pasport"
                  maxLength={12}
                  prefix={
                    <Search
                      size={18}
                      className="text-[#C2C2C2]"
                      aria-hidden="true"
                    />
                  }
                />
              </Form.Item>

              <Text
                style={{
                  fontSize: 12,
                  color: "#999",
                  fontWeight: 500,
                  fontStyle: "italic",
                }}
              >
                Contoh: 800505125001 atau S00123. Tanpa sempang.
              </Text>
            </Flex>

            <Button
              htmlType="submit"
              size="large"
              type="primary"
              loading={loading}
              disabled={loading || !hasIdentityNo}
              block
            >
              Semak rekod
            </Button>
          </Flex>
        </Form>
      </Card>
    </>
  );
}
